import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import { Users, BookOpen, TrendingUp, Activity, UserCog, HeartHandshake, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import LearningTimeOverview from "../../components/LearningTimeOverview";

export default function AdminOverview() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data));
    }, []);

    if (!stats) {
        return <div className="h-64 glass-card rounded-3xl animate-pulse" />;
    }

    return (
        <div className="space-y-6">
            {/* Iter-27.63 — Bandeau d'accès à l'Espace Partenaires (admin séparé) */}
            <Link
                to="/admin/espace-partenaires"
                data-testid="admin-link-partner-spaces"
                className="block rounded-3xl bg-gradient-to-r from-shariti-yellow/15 via-shariti-yellow/8 to-transparent border border-shariti-yellow/30 p-5 hover:border-shariti-yellow/60 transition-all group"
            >
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-shariti-yellow/20 flex items-center justify-center shrink-0">
                        <HeartHandshake className="w-6 h-6 text-shariti-yellow" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow mb-0.5">
                            Backoffice dédié
                        </p>
                        <h3 className="text-base sm:text-lg font-extrabold text-white tracking-tight">
                            Gérer l'Espace Partenaires
                        </h3>
                        <p className="text-xs text-white/60 font-light mt-0.5">
                            Branding, tier, messages de remerciement, histoires d'impact — séparé du contenu de formation.
                        </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-shariti-yellow shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
            </Link>

            <div className="grid grid-cols-12 gap-4 lg:gap-6">
            {[
                { label: "Utilisateurs actifs", value: stats.active_users, icon: Users, color: "#EE722E" },
                { label: "Apprenants", value: stats.learners, icon: UserCog, color: "#F89F63" },
                { label: "Modules", value: stats.total_modules, icon: BookOpen, color: "#FCDB53" },
                { label: "Taux de réussite", value: `${stats.success_rate}%`, icon: TrendingUp, color: "#EE722E" },
            ].map((s, i) => (
                <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="col-span-6 lg:col-span-3 glass-card rounded-3xl p-5 lg:p-6"
                    data-testid={`overview-stat-${i}`}
                >
                    <div
                        className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3"
                        style={{ background: `${s.color}25` }}
                    >
                        <s.icon className="w-5 h-5" color={s.color} />
                    </div>
                    <p className="text-3xl lg:text-4xl font-extrabold text-white mb-1">
                        {s.value}
                    </p>
                    <p className="text-xs font-thin tracking-[0.2em] uppercase text-white/40">
                        {s.label}
                    </p>
                </motion.div>
            ))}

            <div className="col-span-12 lg:col-span-7 glass-card rounded-3xl p-6 lg:p-7">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                            Activité plateforme
                        </p>
                        <h3 className="text-xl font-extrabold text-white">7 derniers jours</h3>
                    </div>
                    <Activity className="w-6 h-6 text-shariti-orange" />
                </div>
                <div className="h-52 -mx-2">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.activity_7d}>
                            <XAxis
                                dataKey="day"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: "#888", fontSize: 11 }}
                            />
                            <Tooltip
                                cursor={{ fill: "rgba(238,114,46,0.05)" }}
                                contentStyle={{
                                    background: "#181818",
                                    border: "1px solid #EE722E",
                                    borderRadius: 12,
                                    fontFamily: "Montserrat",
                                }}
                                labelStyle={{ color: "#EE722E", fontWeight: 800, marginBottom: 4 }}
                                itemStyle={{ color: "#fff", fontWeight: 700 }}
                                formatter={(value) => [`${value} quiz`, null]}
                                separator=""
                            />
                            <Bar dataKey="value" radius={[8, 8, 8, 8]}>
                                {stats.activity_7d.map((_, i) => (
                                    <Cell
                                        key={i}
                                        fill={
                                            i === stats.activity_7d.length - 1
                                                ? "#FCDB53"
                                                : "#EE722E"
                                        }
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="col-span-12 lg:col-span-5 glass-card rounded-3xl p-6 lg:p-7">
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Top performers
                </p>
                <h3 className="text-xl font-extrabold text-white mb-5">Classement live</h3>
                <div className="space-y-2">
                    {stats.top_users.map((u, i) => (
                        <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-xl">
                            <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold bg-shariti-orange/15 text-shariti-orange">
                                {i + 1}
                            </span>
                            {u.avatar ? (
                                <img
                                    src={u.avatar}
                                    alt={u.name}
                                    className="w-8 h-8 rounded-lg object-cover"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-shariti-orange/20 flex items-center justify-center text-sm font-bold text-shariti-orange">
                                    {u.name?.[0]}
                                </div>
                            )}
                            <p className="flex-1 truncate text-sm font-bold text-white">
                                {u.name}
                            </p>
                            <p className="text-sm font-extrabold text-shariti-orange">
                                {u.points}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
            </div>

            {/* Iter-30.16 — Heures réelles de formation (détection speeders) */}
            <LearningTimeOverview />
        </div>
    );
}
